import { CREATE_REVIEW_REQUEST_EVENT, trackEvent } from 'utils/trackingUtils'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { RecipientsSelectorValueType } from 'types'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { Value } from 'components/ShareSheet/RecipientSelector/Types'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useStore } from 'react-redux'
import { InformalFeedbackRequest, InformalFeedbackReviewReviewer } from 'api/clients/InformalFeedbackRequestsClient'
import { useMemberProfile } from 'hooks/useMemberProfile'
import useIsMounted from 'ismounted'
import { InformalFeedbackRequestsService } from 'api/services/AlmanacFiles/InformalFeedbackRequestsService'
import { splitByType } from 'components/ReviewsWizard/AddReviewers/utils'
import { useRefreshInformalFeedbackRequests } from 'hooks/useRefreshInformalFeedbackRequests'
import { useWorkspaceMembers } from 'hooks/useWorkspaceMembers'
import { useIsNotSelf } from 'hooks/useIsNotSelf'
import { uniqBy } from 'lodash'
import { FileWithIdAndType } from 'api/clients/AccessGrantsClient'

export interface SubmitOptions {
  onSuccess?: (request: InformalFeedbackRequest, submitVariables: SubmitVariables) => void
  onError?: () => void
}

export interface SubmitVariables {
  addMembers: boolean
  recipients: Value[]
  title: string
  message: string
  htmlMessage?: string
  endDate: string
  permission?: 'edit' | 'comment' | 'read'
}

export type SubmitHandler = (variables: SubmitVariables, options?: SubmitOptions) => void

interface Props {
  almanacFile: FileWithIdAndType
  onSuccess?: (request: InformalFeedbackRequest, submitVariables: SubmitVariables) => void
}

export const useReviewSubmit = ({ almanacFile, onSuccess }: Props) => {
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const auth = useAuth()
  const isMounted = useIsMounted()
  const currentWorkspace = useCurrentWorkspace()
  const { profile } = useMemberProfile(currentWorkspace?.id, auth?.id)
  const store = useStore()
  const isNotSelf = useIsNotSelf()
  const workspaceMembers = useWorkspaceMembers(currentWorkspace?.key, { includeInactive: false })
  const { refreshRequests } = useRefreshInformalFeedbackRequests({
    almanacFileId: almanacFile.id,
  })

  const submit: SubmitHandler = async (variables) => {
    if (isLoading) return

    const { addMembers, recipients, title, message, htmlMessage, endDate, permission = 'edit' } = variables

    // Set all workspace members as reviewers if workspace group selected
    let workspaceReviewers: InformalFeedbackReviewReviewer[] = []
    if (recipients.find((rec) => rec.type === RecipientsSelectorValueType.Company)) {
      workspaceReviewers = workspaceMembers
        .filter((member) => isNotSelf(member))
        .map((member) => ({ id: member.id, required: false }))
    }

    setIsLoading(true)

    trackEvent(CREATE_REVIEW_REQUEST_EVENT, profile)

    const { users: reviewers, channels: channelReviewers, emails: emailReviewers } = splitByType(recipients)

    const res = await InformalFeedbackRequestsService.create(
      store,
      almanacFile.id,
      {
        subject: title,
        message,
        htmlMessage,
        endsOn: endDate,
        reviewers: uniqBy(reviewers.concat(workspaceReviewers), 'id'),
        emailReviewers,
        channelReviewers,
        permission,
        addMembers,
      },
      queryClient,
    )

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      refreshRequests()
      onSuccess?.(res.data.informal_feedback_request, variables)
    } else {
      reportError(new Error('InformalFeedbackRequest received an error response'), {
        severity: 'error',
        metaData: { info: res.data },
      })
      showErrorMessageToast('Something went wrong, try again')
    }
    if (isMounted.current) setIsLoading(false)
  }

  return { submit, isLoading }
}
